import { useCallback } from 'react';
import { usePageInstanceId } from '../../PageInstanceIdProvider';
import { useDependencyGraphInstanceManager } from '../DependencyGraphInstanceManagerContext';

/**
 * Resolve the dependency graph for the current React tree:
 *   - inside a PageInstanceIdProvider → that instance's graph.
 *   - at page root (no provider / undefined) → the page's base graph.
 *
 * Readiness precondition: when invoked inside a PageInstanceIdProvider, the
 * caller MUST live below a gate that waits for depGraph.useInstanceIds() to
 * include the current instanceId. Otherwise manager.getStore(instanceId) throws
 * on first render. The base graph is frozen for the provider's lifetime (see
 * PageDependencyGraphManagerProvider), so page swaps happen via remount
 * (key change), not in-place mutation.
 *
 * Slot subtree routing: `SlotOutletRunner` wraps its subtree in
 * `<PageInstanceIdProvider value={entry.homeInstanceId}>`, so store lookups
 * inside the slot resolve against the slot's home instance uniformly with all
 * other page-instance routing. `manager.getStore(undefined)` returns baseStore.
 */
export function useDependencyGraphProvider() {
  const instanceId = usePageInstanceId();
  const manager = useDependencyGraphInstanceManager();
  return manager.getStore(instanceId);
}

/** imperative getter: resolve the graph for a specific instanceId (or base when undefined). */
export function useGetDependencyGraphProvider() {
  const manager = useDependencyGraphInstanceManager();
  const getDependencyGraphProvider = useCallback(
    (contextId?: string) => manager.getStore(contextId),
    [manager],
  );
  return { getDependencyGraphProvider };
}
